// Per-section average pass rate, in the author's scorecard sequence.
import React, { useMemo } from 'react'
import { isEmpty } from 'lodash'

import { apiService } from '@/api'

import { colorForPassRate, escapeHtml } from '../helpers/format'
import { ChartSortControl } from '../components/ChartSortControl'
import { useChartSort } from '../hooks/useChartSort'
import {
  ChartWidgetCard,
  axisTooltip,
  hBarBase,
  useEntityClickEvents,
  useWidgetData,
} from './chartWidget'

// "Scorecard order" reproduces the author sequence (scorecard grouped
// alphabetically, then section order) as a single sortable string.
const SORT_OPTIONS = [
  {
    key: 'scorecard',
    label: 'Scorecard order',
    get: (row) => `${row.scorecardName} ${String(row.sectionOrder).padStart(6, '0')}`,
  },
  { key: 'passRate', label: 'Pass rate', get: (row) => row.valuePct },
  { key: 'volume', label: 'Volume', get: (row) => row.callCount },
]

export const SectionPassRateRollupWidget = ({ scope }) => {
  const { data, loading, error } = useWidgetData(
    scope,
    (params) =>
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/section_pass_rates?${params}`
  )

  // Default preserves the author's scorecard sequence (Kyle: "sequence
  // on the scorecard, not by pass rate"); users can re-rank by pass
  // rate or volume. Capped at 15 rows after sorting.
  const sort = useChartSort(SORT_OPTIONS, { defaultKey: 'scorecard', defaultDir: 'asc' })

  const rows = useMemo(
    () =>
      (data?.sections || [])
        .map((section) => {
          const sectionLabel = section.section_name || `Section ${section.section_id}`
          return {
            label: `${sectionLabel} (${section.scorecard_name || section.scorecard_config_sid})`,
            // BE returns [0,1]
            valuePct: Math.round((parseFloat(section.pass_rate) || 0) * 100 * 10) / 10,
            callCount: section.call_count,
            scorecardName: section.scorecard_name || section.scorecard_config_sid || '',
            sectionOrder: Number.isFinite(section.section_order) ? section.section_order : 9999,
            scorecard: {
              scorecard_config_sid: section.scorecard_config_sid,
              sid: section.scorecard_config_sid,
              name: section.scorecard_name,
              config_name: section.scorecard_name,
            },
          }
        })
        .sort(sort.comparator)
        .slice(0, 15),
    [data?.sections, sort.comparator]
  )

  const option = useMemo(() => {
    if (isEmpty(rows)) {
      return {}
    }
    return {
      tooltip: axisTooltip((params) => {
        const row = rows[params[0].dataIndex]
        return `${escapeHtml(row.label)}<br/>Pass rate: ${row.valuePct}%<br/>Calls: ${row.callCount}`
      }),
      ...hBarBase(
        rows.map((row) => row.label),
        { left: 220, xAxis: { min: 0, max: 100, name: '% pass' } }
      ),
      series: [
        {
          type: 'bar',
          cursor: 'pointer',
          data: rows.map((row) => ({
            value: row.valuePct,
            itemStyle: { color: colorForPassRate(row.valuePct) },
            scorecard: row.scorecard,
          })),
        },
      ],
    }
  }, [rows])

  const events = useEntityClickEvents('scorecard', 'scorecard')

  return (
    <ChartWidgetCard
      title="Section Pass Rate Rollup"
      info="Average pass rate for each scorecard section, in the scorecard's author order by default. Click a section to open its scorecard."
      hint="Click a section to open the scorecard drawer"
      error={error}
      empty={isEmpty(rows)}
      emptyText="No section data in this window."
      loading={loading}
      option={option}
      events={events}
      height="380px"
    >
      <ChartSortControl sort={sort} />
    </ChartWidgetCard>
  )
}
